import * as THREE from 'three';
import type { Difficulty, GameSnapshot, RoomState, SonarScanType } from '../../shared/types.js';
import {
  headingDegreesToThreeYawRadians,
  salvageTargetToMapPoint,
  simulationToMapPoint,
  submarineToMapPoint
} from '../../shared/spatial.js';
import { disposeMaterial, disposeObject3D } from './threeUtils.js';
import { projectSalvageTargetToSonarLocal } from '../../shared/viewProjection.js';
import { getSourceGlbTileGridOffset } from '../../shared/maps/tiledGlb.js';
import { concatCollisionMeshes } from '../../shared/maps/mapTileStore.js';
import { DEFAULT_MAP_ID, getMapById, getMapByMissionId, ensureClientTileStore } from './maps/index.js';
import { sampleMapHeight } from './maps/terrainSampling.js';
import type { CollisionMesh } from '../../shared/maps/types.js';
import type { GlbModelMarker, MapDefinition } from './maps/types.js';
import { AnimationLoop } from './AnimationLoop.js';
import { loadGlbModel, loadModelVoxels, type ModelVoxels } from './modelLoader.js';
import { DEFAULT_GRAPHICS_QUALITY, getSonarDotSize, getSonarPointCount, type GraphicsQuality } from './quality.js';
import { sonarPingFade } from '../../shared/constants.js';

/**
 * Crisp sonar dot: a solid disc with a one-or-two-pixel soft rim. The soft radial glow used before
 * made neighbouring dots merge into one pale blob.
 */
function createSolidDotTexture(size = 64): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to create dot texture.');
  }
  ctx.clearRect(0, 0, size, size);
  const gradient = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.86, 'rgba(255,255,255,1)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
  return texture;
}

/** Tiled maps: how far past the sonar radius tiles stay loaded, and how far the sub moves between tile re-checks. */
const SONAR_TILE_MARGIN_METERS = 40;
const SONAR_TILE_RECHECK_METERS = 20;
const DEFAULT_SONAR_RADIUS_METERS = 100;
/** Camera distance as a multiple of the sonar radius, so the scan fills the view at any range. */
const ORBIT_RADIUS_PER_SONAR_METER = 2.0;
/** Solid dots are smaller than the old glow sprites so nearby surfaces stay readable instead of fusing. */
const DOT_SIZE_SCALE = 0.7;
const MAX_SONAR_RADIUS_METERS = 150;
const SONAR_RAY_STEP_METERS = 2.4;

type SonarObstacle =
  | {
      kind: 'cylinder';
      cx: number;
      cz: number;
      radius: number;
      yMin: number;
      yMax: number;
      cr: number;
      cg: number;
      cb: number;
    }
  | {
      kind: 'box';
      cx: number;
      cz: number;
      halfWidth: number;
      halfDepth: number;
      yMin: number;
      yMax: number;
      cr: number;
      cg: number;
      cb: number;
    }
  | {
      kind: 'sphere';
      cx: number;
      cy: number;
      cz: number;
      radius: number;
      cr: number;
      cg: number;
      cb: number;
    }
  | {
      /** A marker model as unit cubes, behind a bounding box so most rays skip it. */
      kind: 'voxels';
      cx: number;
      cz: number;
      halfWidth: number;
      halfDepth: number;
      yMin: number;
      yMax: number;
      cubes: Float32Array; // x, y, z centres (world)
      half: number;
      cr: number;
      cg: number;
      cb: number;
    }
  | {
      kind: 'horizontalCylinder';
      axis: 'x' | 'z';
      cx: number;
      cz: number;
      radius: number;
      halfLength: number;
      cr: number;
      cg: number;
      cb: number;
    };

export type SonarPingHit = {
  x: number;
  y: number;
  z: number;
  r: number;
  g: number;
  b: number;
  isTerrain: boolean;
  resourceType?: string;
};

/**
 * Quickselect: partially orders order[start, end) so order[kth] is the element a full sort by centroid[axis] would
 * place there, with everything before it <= and everything after it >=.
 */
function selectByCentroid(order: number[], centroids: Float32Array, axis: number, start: number, end: number, kth: number): void {
  let lo = start;
  let hi = end - 1;
  while (lo < hi) {
    const pivot = centroids[order[(lo + hi) >> 1] * 3 + axis];
    let i = lo;
    let j = hi;
    while (i <= j) {
      while (centroids[order[i] * 3 + axis] < pivot) i += 1;
      while (centroids[order[j] * 3 + axis] > pivot) j -= 1;
      if (i <= j) {
        const swap = order[i];
        order[i] = order[j];
        order[j] = swap;
        i += 1;
        j -= 1;
      }
    }
    if (kth <= j) hi = j;
    else if (kth >= i) lo = i;
    else return;
  }
}

type CollisionMeshBVHNode = {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
  start: number;
  count: number;
  left: number | null;
  right: number | null;
};

type NormalizedCollisionMesh = CollisionMesh & {
  bvhNodes: CollisionMeshBVHNode[];
  triangleIndices: number[];
  triangleAABBs: Float32Array;
  triangleCentroids: Float32Array;
  minY: number;
  tileY: number | null;
};

export class SonarScanView {
  private readonly container: HTMLElement;
  private activeMap: MapDefinition;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(62, 1, 0.1, 320);
  private readonly renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  private readonly quality: GraphicsQuality;
  private readonly maxSonarPointCount: number;
  private sonarPointCount: number;
  private rayDirections: THREE.Vector3[];
  private readonly scanPointPositions: Float32Array;
  private readonly scanPointColors: Float32Array;
  private readonly scanPointDistances: Float32Array;
  private lastCapturedPingHits: SonarPingHit[] = [];
  private sonarRadiusMeters = DEFAULT_SONAR_RADIUS_METERS;
  private readonly modelVoxels = new Map<string, ModelVoxels>();
  private readonly modelBoundsLoading = new Map<string, Promise<void>>();
  private collisionMesh: NormalizedCollisionMesh | null = null;
  /** Tiled maps: which loaded tiles `collisionMesh` was merged from, and where the sub was when that was last checked. */
  private tileMeshSignature = '';
  private tileMeshCheckX = Number.NaN;
  private tileMeshCheckZ = Number.NaN;
  private tileMeshCheckY = Number.NaN;
  private readonly scanPointGeometry = new THREE.BufferGeometry();
  private readonly scanPointMaterial = new THREE.PointsMaterial({
    map: createSolidDotTexture(64),
    alphaTest: 0.5,
    size: 1.65,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,
    vertexColors: true,
    depthWrite: true,
    toneMapped: false
  });
  private readonly scanPoints = new THREE.Points(this.scanPointGeometry, this.scanPointMaterial);
  private readonly dockingPointGeometry = new THREE.BufferGeometry();
  private readonly dockingPointMaterial = new THREE.PointsMaterial({
    map: createSolidDotTexture(64),
    alphaTest: 0.5,
    size: 3.2,
    sizeAttenuation: true,
    color: 0xff3a30,
    transparent: true,
    opacity: 0.95,
    depthWrite: true,
    toneMapped: false
  });
  private readonly dockingPointCloud = new THREE.Points(this.dockingPointGeometry, this.dockingPointMaterial);
  private readonly resourcePointGeometry = new THREE.BufferGeometry();
  private readonly resourcePointMaterial = new THREE.PointsMaterial({
    map: createSolidDotTexture(64),
    alphaTest: 0.5,
    size: 1.8,
    sizeAttenuation: true,
    transparent: true,
    opacity: 1,
    vertexColors: true,
    depthWrite: true,
    toneMapped: false
  });
  private readonly resourcePoints = new THREE.Points(this.resourcePointGeometry, this.resourcePointMaterial);
  private resourcePointPositions: Float32Array;
  private resourcePointColors: Float32Array;
  private readonly resourcePointDistances: Float32Array;
  private resourceFrozenHitCount = 0;
  private readonly scanPulse = new THREE.Mesh(
    new THREE.RingGeometry(0.65, 0.9, 42),
    new THREE.MeshBasicMaterial({ color: 0x8bf7ff, transparent: true, opacity: 0.42, side: THREE.DoubleSide })
  );
  private readonly waypointDot = new THREE.Mesh(
    new THREE.SphereGeometry(1.85, 18, 14),
    new THREE.MeshBasicMaterial({ color: 0xff6b5c, transparent: true, opacity: 0, blending: THREE.AdditiveBlending })
  );
  private rangeRing: THREE.Mesh;

  private getSonarRadius(): number {
    return this.sonarRadiusMeters ?? this.activeMap.sonarRadiusMeters ?? DEFAULT_SONAR_RADIUS_METERS;
  }

  private getScaledSonarPointCount(sonarRadius: number): number {
    return Math.max(32, Math.round(getSonarPointCount(this.quality) * (sonarRadius / DEFAULT_SONAR_RADIUS_METERS)));
  }

  public setSonarRadiusMeters(value: number): void {
    this.sonarRadiusMeters = THREE.MathUtils.clamp(value, 30, MAX_SONAR_RADIUS_METERS);
    this.sonarPointCount = this.getScaledSonarPointCount(this.getSonarRadius());
    this.rayDirections = this.buildRayDirections(this.sonarPointCount);
    // Neighbouring points are ~sqrt(radius) apart (count grows with radius, area with radius squared),
    // so size the dots the same way: solid, separate dots at every range instead of a blur up close.
    this.scanPointMaterial.size = this.defaultDotSize * DOT_SIZE_SCALE * Math.sqrt(this.getSonarRadius() / DEFAULT_SONAR_RADIUS_METERS);
    this.orbitRadius = THREE.MathUtils.clamp(this.getSonarRadius() * ORBIT_RADIUS_PER_SONAR_METER, this.orbitMinRadius, this.orbitMaxRadius);
    this.updateRangeRingGeometry();
  }

  private updateRangeRingGeometry(): void {
    const radius = this.getSonarRadius();
    this.rangeRing.geometry.dispose();
    this.rangeRing.geometry = new THREE.RingGeometry(Math.max(radius - 0.9, 0.01), radius, 96);
    this.compassWorldPositions.N.set(0, 0, -radius);
    this.compassWorldPositions.E.set(radius, 0, 0);
    this.compassWorldPositions.S.set(0, 0, radius);
    this.compassWorldPositions.W.set(-radius, 0, 0);
  }

  private readonly frozenScanGroup = new THREE.Group();
  private readonly submarineMarker = new THREE.Group();
  private readonly compassOverlay = document.createElement('div');
  private readonly compassLabels: Record<'N' | 'E' | 'S' | 'W', HTMLSpanElement> = {
    N: document.createElement('span'),
    E: document.createElement('span'),
    S: document.createElement('span'),
    W: document.createElement('span')
  };
  // Fixed world-space anchors for compass labels (North=-Z, East=+X: heading 0 points toward -Z, heading 90 toward +X, see spatial.ts getBearingToMapPoint).
  // Placed at submarine Y level so they orbit the sub when camera rotates.
  private readonly compassWorldPositions: Record<'N' | 'E' | 'S' | 'W', THREE.Vector3> = {
    N: new THREE.Vector3(0, 0, -10),
    E: new THREE.Vector3(10, 0, 0),
    S: new THREE.Vector3(0, 0, 10),
    W: new THREE.Vector3(-10, 0, 0)
  };
  private readonly resizeObserver: ResizeObserver;

  private animationLoop: AnimationLoop | null = null;
  private pingCapturedAt = 0;
  private difficulty: Difficulty = 'normal';
  private frozenHitCount = 0;
  private frozenCenterX = 0;
  private frozenCenterY = 0;
  private frozenCenterZ = 0;
  private currentYawRad = 0;
  private defaultDotSize = 1.65;

  // Orbit camera state (spherical coords centred on origin)
  private orbitTheta = 0.38;       // azimuth radians
  private orbitPhi = 1.02;         // polar radians (0 = top, PI = bottom)
  private orbitRadius = 115;       // distance from origin
  private readonly orbitMinPhi = 0.12;
  private readonly orbitMaxPhi = Math.PI - 0.12;
  private readonly orbitMinRadius = 20;
  private readonly orbitMaxRadius = 260;

  // Touch and mouse tracking
  private touchStartX = 0;
  private touchStartY = 0;
  private touchPrevPinchDist = 0;
  private isDragging = false;
  private isMouseDown = false;

  // Thruster bubble particles
  private readonly thrusterBubbleGroup = new THREE.Group();
  private readonly bubbleGeometry = new THREE.IcosahedronGeometry(0.05, 0);
  private readonly bubbleMaterialPrototype = new THREE.MeshBasicMaterial({ color: 0x7dfff0, transparent: true, opacity: 0.55, depthWrite: false });
  private readonly bubblePool: Array<{
    mesh: THREE.Mesh;
    life: number;      // elapsed seconds
    maxLife: number;
    vx: number; vy: number; vz: number;
    baseVy: number;
  }> = [];
  private lastBubbleEmitMs = 0;
  private lastSnapshot: import('../../shared/types.js').GameSnapshot | null = null;

  constructor(container: HTMLElement, quality: GraphicsQuality = DEFAULT_GRAPHICS_QUALITY) {
    this.container = container;
    this.activeMap = getMapById(DEFAULT_MAP_ID) ?? {
      id: DEFAULT_MAP_ID,
      name: 'default',
      sizeMeters: { width: 140, height: 140 },
      resolution: 1,
      seed: 0,
      baseDepth: 0,
      noiseScale: { a: 0, b: 0, c: 0 },
      mountains: [],
      caverns: [],
      boulderFields: [],
      hydrothermalVents: [],
      seaweedClusters: [],
      currentZones: [],
      missionLayout: {
        submarineSpawn: { x: 0, z: 0, depth: 0, reference: 'surface' }
      }
    };
    this.quality = quality;
    this.maxSonarPointCount = this.getScaledSonarPointCount(MAX_SONAR_RADIUS_METERS);
    this.sonarPointCount = this.getScaledSonarPointCount(this.getSonarRadius());
    this.defaultDotSize = getSonarDotSize(quality);
    this.scanPointMaterial.size = this.defaultDotSize * DOT_SIZE_SCALE * Math.sqrt(this.getSonarRadius() / DEFAULT_SONAR_RADIUS_METERS);
    this.orbitRadius = THREE.MathUtils.clamp(this.getSonarRadius() * ORBIT_RADIUS_PER_SONAR_METER, this.orbitMinRadius, this.orbitMaxRadius);
    this.rayDirections = this.buildRayDirections(this.sonarPointCount);
    this.scanPointPositions = new Float32Array(this.maxSonarPointCount * 3);
    this.scanPointColors = new Float32Array(this.maxSonarPointCount * 3);
    this.scanPointDistances = new Float32Array(this.maxSonarPointCount);

    this.scene.background = new THREE.Color(0x01070b);

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.92;
    this.renderer.domElement.className = 'sonar-scan-canvas';
    this.container.style.position = 'relative';
    this.compassOverlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;user-select:none;';
    for (const key of Object.keys(this.compassLabels) as Array<keyof typeof this.compassLabels>) {
      const label = this.compassLabels[key];
      label.textContent = key;
      if (key === 'N') {
        label.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);color:rgba(255,80,80,1);font:900 16px Segoe UI,sans-serif;letter-spacing:0.12em;text-shadow:0 0 10px rgba(255,60,60,0.8);';
      } else {
        label.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);color:rgba(202,255,245,0.92);font:700 12px Segoe UI,sans-serif;letter-spacing:0.16em;text-shadow:0 0 8px rgba(25,219,185,0.55);';
      }
      this.compassOverlay.append(label);
    }

    this.container.replaceChildren(this.renderer.domElement, this.compassOverlay);

    this.scanPointGeometry.setAttribute('position', new THREE.BufferAttribute(this.scanPointPositions, 3));
    this.scanPointGeometry.setAttribute('color', new THREE.BufferAttribute(this.scanPointColors, 3));
    this.scanPointGeometry.setDrawRange(0, 0);
    this.resourcePointPositions = new Float32Array(this.maxSonarPointCount * 3);
    this.resourcePointColors = new Float32Array(this.maxSonarPointCount * 3);
    this.resourcePointDistances = new Float32Array(this.maxSonarPointCount);
    this.resourcePointGeometry.setAttribute('position', new THREE.BufferAttribute(this.resourcePointPositions, 3));
    this.resourcePointGeometry.setAttribute('color', new THREE.BufferAttribute(this.resourcePointColors, 3));
    this.resourcePointGeometry.setDrawRange(0, 0);
    this.dockingPointGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(0), 3));
    this.dockingPointGeometry.setDrawRange(0, 0);
    this.frozenScanGroup.add(this.scanPoints, this.resourcePoints);
    this.scene.add(this.frozenScanGroup);

    this.scanPulse.rotation.x = -Math.PI / 2;
    this.scanPulse.position.set(0, -0.12, 0);
    this.waypointDot.position.set(0, -4, 0);
    this.rangeRing = new THREE.Mesh(
      new THREE.RingGeometry(this.getSonarRadius() - 0.9, this.getSonarRadius(), 96),
      new THREE.MeshBasicMaterial({ color: 0x28d8ae, transparent: true, opacity: 0.22, side: THREE.DoubleSide })
    );
    this.rangeRing.rotation.x = -Math.PI / 2;
    this.rangeRing.position.set(0, -0.2, 0);
    // Initialise compass anchors to match the default sonar radius
    this.updateRangeRingGeometry();
    this.buildSubmarineMarker();
    void this.loadSubmarineGlbModel();
    this.preloadModelBounds(this.activeMap);
    this.frozenScanGroup.add(this.waypointDot);
    this.scene.add(this.scanPulse, this.rangeRing, this.submarineMarker, this.dockingPointCloud, this.thrusterBubbleGroup);

    const ambient = new THREE.AmbientLight(0x3dc98f, 0.24);
    const keyLight = new THREE.DirectionalLight(0x8cefd0, 0.55);
    keyLight.position.set(8, 14, 5);
    const fill = new THREE.PointLight(0x31f1b8, 0.44, 30, 1.6);
    fill.position.set(-7, 5, -8);
    this.scene.add(ambient, keyLight, fill);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();

    this.bindTouchControls();

    this.animate = this.animate.bind(this);
    this.animationLoop = new AnimationLoop(this.animate, 60);
    this.animationLoop.start();
  }

  capture(snapshot: GameSnapshot, scanType: SonarScanType = 'terrain'): void {
    this.lastSnapshot = snapshot;
    this.refreshTiledCollisionMesh(snapshot, true);
    if (scanType === 'resource') {
      this.captureResourceScan(snapshot);
    } else if (scanType === 'subsurface') {
      this.captureSubsurfaceScan(snapshot);
    } else {
      this.captureTerrainScan(snapshot);
    }
    this.applyLiveState(snapshot);
    this.pingCapturedAt = performance.now();
  }

  setSnapshot(snapshot: GameSnapshot): void {
    this.lastSnapshot = snapshot;
    this.refreshTiledCollisionMesh(snapshot, false);
    this.applyLiveState(snapshot);
    if (this.pingCapturedAt === 0 && snapshot.world.sonarContact) {
      // Preserve an existing active sonar scan only when the game already has an active
      // sonar contact. Do not auto-capture a ping on initial room entry.
      this.captureCombinedScan(snapshot);
      this.pingCapturedAt = performance.now();
    }
  }

  /**
   * Tiled maps: keep `collisionMesh` (the thing the scans ray-cast against) merged from the loaded tiles around the
   * sub, loading tiles just in time. Cheap when nothing changed; re-merging only happens when the set of nearby loaded
   * tiles changes.
   */
  private refreshTiledCollisionMesh(snapshot: GameSnapshot, force: boolean): void {
    const store = ensureClientTileStore(this.activeMap);
    if (!store) return;
    const point = submarineToMapPoint(snapshot.submarine);
    const worldY = -snapshot.submarine.depth;
    if (!force && Math.hypot(point.x - this.tileMeshCheckX, point.z - this.tileMeshCheckZ, worldY - this.tileMeshCheckY) < SONAR_TILE_RECHECK_METERS) return;
    this.tileMeshCheckX = point.x;
    this.tileMeshCheckZ = point.z;
    this.tileMeshCheckY = worldY;
    const radius = this.getSonarRadius() + SONAR_TILE_MARGIN_METERS;
    const map = this.activeMap;
    void store.ensureNear(point.x, point.z, radius, worldY).then(() => {
      if (this.activeMap === map) this.applyTiledCollisionMesh(point.x, point.z, radius, worldY);
    }).catch(() => undefined);
    this.applyTiledCollisionMesh(point.x, point.z, radius, worldY);
  }

  private applyTiledCollisionMesh(x: number, z: number, radius: number, worldY: number): void {
    const store = this.activeMap.tileStore;
    if (!store) return;
    const signature = store.tilesNear(x, z, radius, worldY).filter((entry) => store.isLoaded(entry.key)).map((entry) => entry.key).sort().join('|');
    if (signature === this.tileMeshSignature) return;
    this.tileMeshSignature = signature;
    this.collisionMesh = this.normalizeCollisionMesh(concatCollisionMeshes(store.meshesNear(x, z, radius, worldY)), this.activeMap.sourceGlbTileSizeMeters?.Y ?? null);
  }

  private applyLiveState(snapshot: GameSnapshot): void {
    const submarine = submarineToMapPoint(snapshot.submarine);
    this.currentYawRad = headingDegreesToThreeYawRadians(snapshot.submarine.yaw);
    this.submarineMarker.rotation.y = this.currentYawRad;
    // Shift frozen scan hits so they remain aligned to the current submarine position.
    // frozenCenter* is where the sub was when the scan fired; the group offset corrects
    // for any movement since then so the terrain matches what the camera shows.
    if (this.pingCapturedAt > 0) {
      this.frozenScanGroup.position.set(
        this.frozenCenterX - submarine.x,
        this.frozenCenterY - (-snapshot.submarine.depth),
        this.frozenCenterZ - submarine.z
      );
    }
    this.updateDockingPointDots(snapshot);
  }

  private updateDockingPointDots(snapshot: GameSnapshot): void {
    const dockingMarkers = (this.activeMap.glbModelMarkers ?? []).filter((marker) => marker.model.toLowerCase() === 'docking_port');
    const submarinePoint = submarineToMapPoint(snapshot.submarine);
    const worldOriginY = -snapshot.submarine.depth;
    const sonarRadius = this.getSonarRadius();
    const positions = new Float32Array(dockingMarkers.length * 3);
    let count = 0;

    for (const marker of dockingMarkers) {
      const localX = marker.mapX - submarinePoint.x;
      const localZ = marker.mapZ - submarinePoint.z;
      const localY = -marker.depth - worldOriginY;
      if (Math.hypot(localX, localY, localZ) > sonarRadius) {
        continue;
      }
      const offset = count * 3;
      positions[offset] = localX;
      positions[offset + 1] = localY;
      positions[offset + 2] = localZ;
      count += 1;
    }

    this.dockingPointGeometry.setAttribute('position', new THREE.BufferAttribute(positions.subarray(0, count * 3), 3));
    this.dockingPointGeometry.setDrawRange(0, count);
    this.dockingPointGeometry.computeBoundingSphere();
  }

  private captureTerrainScan(snapshot: GameSnapshot): void {
    this.commitCapturedHits(snapshot, this.collectTerrainScanHits(snapshot));
  }

  private captureResourceScan(snapshot: GameSnapshot): void {
    this.commitCapturedHits(snapshot, this.collectResourceScanHits(snapshot));
  }

  private captureSubsurfaceScan(snapshot: GameSnapshot): void {
    this.commitCapturedHits(snapshot, this.collectAllTerrainScanHits(snapshot));
  }

  private captureCombinedScan(snapshot: GameSnapshot): void {
    const terrainHits = this.collectTerrainScanHits(snapshot);
    const resourceHits = this.collectResourceScanHits(snapshot);
    this.commitCapturedHits(snapshot, [...terrainHits, ...resourceHits]);
  }

  private collectTerrainScanHits(snapshot: GameSnapshot): Array<{
    distance: number;
    localX: number;
    localY: number;
    localZ: number;
    r: number;
    g: number;
    b: number;
    isTerrain: boolean;
  }> {
    const submarine = submarineToMapPoint(snapshot.submarine);
    this.frozenCenterX = submarine.x;
    this.frozenCenterZ = submarine.z;
    const worldOriginY = -snapshot.submarine.depth;
    this.frozenCenterY = worldOriginY;

    const hits: Array<{ distance: number; localX: number; localY: number; localZ: number; r: number; g: number; b: number; isTerrain: boolean }> = [];
    const obstacles = this.buildObstacles(worldOriginY);

    for (let index = 0; index < this.sonarPointCount; index += 1) {
      const localDirection = this.rayDirections[index];
      const worldDirection = localDirection.clone();
      const terrainDist = this.findFirstHitDistance(this.frozenCenterX, worldOriginY, this.frozenCenterZ, worldDirection);
      const meshDist = this.collisionMesh
        ? this.findCollisionMeshHitDistance(this.frozenCenterX, worldOriginY, this.frozenCenterZ, worldDirection, this.collisionMesh)
        : null;
      const volumeDist = this.findCollisionVolumeHitDistance(this.frozenCenterX, worldOriginY, this.frozenCenterZ, worldDirection);
      const objHit = this.checkObjectHit(this.frozenCenterX, worldOriginY, this.frozenCenterZ, worldDirection, obstacles);

      let hitDistance: number;
      let r: number, g: number, b: number;
      let isTerrain = false;
      const hasCollisionMesh = this.collisionMesh !== null;

      if (meshDist !== null && (volumeDist === null || meshDist <= volumeDist) && (objHit === null || meshDist <= objHit.distance)) {
        hitDistance = meshDist;
        const ratio = hitDistance / this.getSonarRadius();
        r = THREE.MathUtils.lerp(0.2, 0.55, ratio);
        g = THREE.MathUtils.lerp(0.9, 0.72, ratio);
        b = THREE.MathUtils.lerp(1, 0.58, ratio);
      } else if (volumeDist !== null && (objHit === null || volumeDist <= objHit.distance) && (!hasCollisionMesh || terrainDist === null || volumeDist <= terrainDist)) {
        hitDistance = volumeDist;
        const ratio = hitDistance / this.getSonarRadius();
        r = THREE.MathUtils.lerp(0.2, 0.55, ratio);
        g = THREE.MathUtils.lerp(0.9, 0.72, ratio);
        b = THREE.MathUtils.lerp(1, 0.58, ratio);
      } else if ((!hasCollisionMesh || meshDist === null) && terrainDist !== null && (objHit === null || terrainDist <= objHit.distance)) {
        hitDistance = terrainDist;
        isTerrain = true;
        const ratio = hitDistance / this.getSonarRadius();
        r = THREE.MathUtils.lerp(0.16, 0.48, ratio);
        g = THREE.MathUtils.lerp(0.98, 0.78, ratio);
        b = THREE.MathUtils.lerp(1, 0.62, ratio);
      } else if (objHit !== null) {
        hitDistance = objHit.distance;
        r = objHit.r; g = objHit.g; b = objHit.b;
      } else {
        continue;
      }

      const localHit = localDirection.clone().multiplyScalar(hitDistance);
      hits.push({ distance: hitDistance, localX: localHit.x, localY: localHit.y, localZ: localHit.z, r, g, b, isTerrain });
    }

    return hits;
  }

  public getLastCapturedPingHits(): SonarPingHit[] {
    return this.lastCapturedPingHits.slice();
  }

  // Like collectTerrainScanHits but continues marching through terrain to collect all
  // surface crossings per ray, revealing caves, overhangs, and sub-surface geometry.
  private collectAllTerrainScanHits(snapshot: GameSnapshot): Array<{
    distance: number;
    localX: number;
    localY: number;
    localZ: number;
    r: number;
    g: number;
    b: number;
    isTerrain: boolean;
  }> {
    const submarine = submarineToMapPoint(snapshot.submarine);
    this.frozenCenterX = submarine.x;
    this.frozenCenterZ = submarine.z;
    const worldOriginY = -snapshot.submarine.depth;
    this.frozenCenterY = worldOriginY;
    const sonarRadius = this.getSonarRadius();
    const iterations = Math.ceil(sonarRadius / SONAR_RAY_STEP_METERS);

    const hits: Array<{ distance: number; localX: number; localY: number; localZ: number; r: number; g: number; b: number; isTerrain: boolean }> = [];

    // Collect all BVH mesh hit distances for the subsurface scan.
    const findAllMeshHits = (worldDirection: THREE.Vector3): number[] => {
      if (!this.collisionMesh) return [];
      const { positions, indices, bvhNodes, triangleIndices } = this.collisionMesh;
      if (bvhNodes.length === 0) return [];
      const allHits: number[] = [];
      const stack: number[] = [0];
      while (stack.length > 0) {
        const node = bvhNodes[stack.pop()!];
        if (!this.rayIntersectsAABB(
          this.frozenCenterX, worldOriginY, this.frozenCenterZ,
          worldDirection.x, worldDirection.y, worldDirection.z,
          node.minX, node.minY, node.minZ, node.maxX, node.maxY, node.maxZ, sonarRadius
        )) continue;
        if (node.left !== null && node.right !== null) {
          stack.push(node.right);
          stack.push(node.left);
          continue;
        }
        const endIndex = node.start + node.count;
        for (let i = node.start; i < endIndex; i += 1) {
          const tri = triangleIndices[i] * 3;
          const ai = indices[tri] * 3;
          const bi = indices[tri + 1] * 3;
          const ci = indices[tri + 2] * 3;
          const pax = positions[ai]; const pay = positions[ai + 1]; const paz = positions[ai + 2];
          const pbx = positions[bi]; const pby = positions[bi + 1]; const pbz = positions[bi + 2];
          const pcx = positions[ci]; const pcy = positions[ci + 1]; const pcz = positions[ci + 2];
          if (this.isTileBoundaryCap(pay, pby, pcy, this.collisionMesh!)) continue;
          const t = this.rayIntersectTriangle(
            this.frozenCenterX, worldOriginY, this.frozenCenterZ,
            worldDirection.x, worldDirection.y, worldDirection.z,
            pax, pay, paz, pbx, pby, pbz, pcx, pcy, pcz
          );
          if (t !== null && t >= 0.5 && t <= sonarRadius) {
            allHits.push(t);
          }
        }
      }
      // Deduplicate hits that are very close together (same triangle front/back)
      allHits.sort((a, b) => a - b);
      const deduped: number[] = [];
      for (const t of allHits) {
        if (deduped.length === 0 || t - deduped[deduped.length - 1] > 0.3) {
          deduped.push(t);
        }
      }
      return deduped;
    };

    for (let index = 0; index < this.sonarPointCount; index += 1) {
      const localDirection = this.rayDirections[index];
      const worldDirection = localDirection.clone();

      // Collect all heightmap crossings
      const originDelta = worldOriginY - this.sampleFloorWorldY(this.frozenCenterX, this.frozenCenterZ);
      let previousDelta = originDelta;
      let previousDistance = 0;

      const addHeightmapHit = (hitDistance: number): void => {
        const ratio = hitDistance / sonarRadius;
        const r = THREE.MathUtils.lerp(0.16, 0.48, ratio);
        const g = THREE.MathUtils.lerp(0.98, 0.78, ratio);
        const b = THREE.MathUtils.lerp(1, 0.62, ratio);
        const localHit = localDirection.clone().multiplyScalar(hitDistance);
        hits.push({ distance: hitDistance, localX: localHit.x, localY: localHit.y, localZ: localHit.z, r, g, b, isTerrain: true });
      };

      if (this.collisionMesh) {
        // Use BVH mesh — collect all intersections
        for (const t of findAllMeshHits(worldDirection)) {
          const ratio = t / sonarRadius;
          const r = THREE.MathUtils.lerp(0.2, 0.55, ratio);
          const g = THREE.MathUtils.lerp(0.9, 0.72, ratio);
          const b = THREE.MathUtils.lerp(1, 0.58, ratio);
          const localHit = localDirection.clone().multiplyScalar(t);
          hits.push({ distance: t, localX: localHit.x, localY: localHit.y, localZ: localHit.z, r, g, b, isTerrain: true });
        }
      } else {
        // Heightmap march — collect all sign changes
        for (let i = 1; i <= iterations; i += 1) {
          const distance = (i / iterations) * sonarRadius;
          const sampleX = this.frozenCenterX + worldDirection.x * distance;
          const sampleY = worldOriginY + worldDirection.y * distance;
          const sampleZ = this.frozenCenterZ + worldDirection.z * distance;
          const delta = sampleY - this.sampleFloorWorldY(sampleX, sampleZ);

          if (previousDelta > 0 && delta <= 0) {
            // Entering terrain
            let near = previousDistance;
            let far = distance;
            for (let ref = 0; ref < 4; ref += 1) {
              const mid = (near + far) * 0.5;
              const midX = this.frozenCenterX + worldDirection.x * mid;
              const midY = worldOriginY + worldDirection.y * mid;
              const midZ = this.frozenCenterZ + worldDirection.z * mid;
              if (midY - this.sampleFloorWorldY(midX, midZ) > 0) near = mid; else far = mid;
            }
            addHeightmapHit(far);
          } else if (previousDelta <= 0 && delta > 0) {
            // Exiting terrain — add back-face hit for cave visibility
            addHeightmapHit((previousDistance + distance) * 0.5);
          }

          previousDelta = delta;
          previousDistance = distance;
        }
      }
    }

    return hits;
  }

  private resourceTypeToColor(type: string): { cr: number; cg: number; cb: number } {
    switch (type.toLowerCase()) {
      case 'fuel':
      case 'carbon': return { cr: 0.98, cg: 0.72, cb: 0.12 };
      case 'crystal':
      case 'silica':
      case 'halite':
      case 'lithium salt': return { cr: 0.12, cg: 0.98, cb: 0.88 };
      case 'mineral':
      case 'bio-sludge':
      case 'sulphur': return { cr: 0.22, cg: 0.95, cb: 0.35 };
      case 'metal':
      case 'iron ore':
      case 'copper ore':
      case 'bauxite':
      case 'cobalt ore':
      case 'tungsten ore':
      case 'iridium': return { cr: 0.72, cg: 0.88, cb: 1.00 };
      default:        return { cr: 0.90, cg: 0.25, cb: 0.95 };
    }
  }

  private collectResourceScanHits(snapshot: GameSnapshot): Array<{
    distance: number;
    localX: number;
    localY: number;
    localZ: number;
    worldX: number;
    worldY: number;
    worldZ: number;
    r: number;
    g: number;
    b: number;
    isTerrain: false;
    resourceType: string;
  }> {
    const submarine = submarineToMapPoint(snapshot.submarine);
    this.frozenCenterX = submarine.x;
    this.frozenCenterZ = submarine.z;
    const worldOriginY = -snapshot.submarine.depth;
    this.frozenCenterY = worldOriginY;
    const sonarRadius = this.getSonarRadius();

    const hits: Array<{
      distance: number;
      localX: number;
      localY: number;
      localZ: number;
      worldX: number;
      worldY: number;
      worldZ: number;
      r: number;
      g: number;
      b: number;
      isTerrain: false;
      resourceType: string;
    }> = [];

    for (const node of snapshot.world.resourceNodes) {
      if (node.resourceValue <= 0) continue;

      const nodePt = simulationToMapPoint({ x: node.simulationX, y: node.simulationZ });
      const nodeWorldY = -node.depth;
      const dx = nodePt.x - this.frozenCenterX;
      const dy = nodeWorldY - worldOriginY;
      const dz = nodePt.z - this.frozenCenterZ;
      const dist = Math.hypot(dx, dy, dz);
      if (dist > sonarRadius) continue;

      const { cr, cg, cb } = this.resourceTypeToColor(node.resourceType);
      const intensity = THREE.MathUtils.clamp(node.resourceValue / 20, 0.2, 1.0);

      hits.push({
        distance: dist,
        localX: dx, localY: dy, localZ: dz,
        worldX: nodePt.x,
        worldY: nodeWorldY,
        worldZ: nodePt.z,
        r: cr * intensity,
        g: cg * intensity,
        b: cb * intensity,
        isTerrain: false,
        resourceType: node.resourceType
      });
    }

    return hits;
  }

  private commitCapturedHits(
    snapshot: GameSnapshot,
    hits: Array<{
      distance: number;
      localX: number;
      localY: number;
      localZ: number;
      r: number;
      g: number;
      b: number;
      isTerrain: boolean;
      resourceType?: string;
      worldX?: number;
      worldY?: number;
      worldZ?: number;
    }>
  ): void {
    const worldOriginY = -snapshot.submarine.depth;
    hits.sort((a, b) => a.distance - b.distance);

    const terrainHits = hits.filter((hit) => hit.isTerrain);
    const resourceHits = hits.filter((hit) => !hit.isTerrain);

    const terrainCount = Math.min(terrainHits.length, this.sonarPointCount);
    for (let index = 0; index < terrainCount; index += 1) {
      const hit = terrainHits[index];
      this.scanPointPositions[index * 3] = hit.localX;
      this.scanPointPositions[index * 3 + 1] = hit.localY;
      this.scanPointPositions[index * 3 + 2] = hit.localZ;
      this.scanPointColors[index * 3] = hit.r;
      this.scanPointColors[index * 3 + 1] = hit.g;
      this.scanPointColors[index * 3 + 2] = hit.b;
      this.scanPointDistances[index] = hit.distance;
    }

    const resourceCount = Math.min(resourceHits.length, this.sonarPointCount);
    for (let index = 0; index < resourceCount; index += 1) {
      const hit = resourceHits[index];
      this.resourcePointPositions[index * 3] = hit.localX;
      this.resourcePointPositions[index * 3 + 1] = hit.localY;
      this.resourcePointPositions[index * 3 + 2] = hit.localZ;
      this.resourcePointColors[index * 3] = hit.r;
      this.resourcePointColors[index * 3 + 1] = hit.g;
      this.resourcePointColors[index * 3 + 2] = hit.b;
      this.resourcePointDistances[index] = hit.distance;
    }

    this.frozenHitCount = terrainCount;
    this.resourceFrozenHitCount = resourceCount;
    const selectedHits = [...terrainHits.slice(0, terrainCount), ...resourceHits.slice(0, resourceCount)];
    this.lastCapturedPingHits = selectedHits.map((hit) => ({
      x: hit.worldX ?? this.frozenCenterX + hit.localX,
      y: hit.worldY ?? worldOriginY + hit.localY,
      z: hit.worldZ ?? this.frozenCenterZ + hit.localZ,
      r: hit.r,
      g: hit.g,
      b: hit.b,
      isTerrain: hit.isTerrain,
      resourceType: hit.resourceType
    }));

    const pointPositions = this.scanPointGeometry.getAttribute('position') as THREE.BufferAttribute;
    const pointColors = this.scanPointGeometry.getAttribute('color') as THREE.BufferAttribute;
    pointPositions.needsUpdate = true;
    pointColors.needsUpdate = true;
    this.scanPointGeometry.setDrawRange(0, terrainCount);
    this.scanPointGeometry.computeBoundingSphere();

    const resourcePositions = this.resourcePointGeometry.getAttribute('position') as THREE.BufferAttribute;
    const resourceColors = this.resourcePointGeometry.getAttribute('color') as THREE.BufferAttribute;
    resourcePositions.needsUpdate = true;
    resourceColors.needsUpdate = true;
    this.resourcePointGeometry.setDrawRange(0, resourceCount);
    this.resourcePointGeometry.computeBoundingSphere();

    this.scanPulse.position.set(0, -0.12, 0);

    if (snapshot.world.sonarContact) {
      const target = salvageTargetToMapPoint(snapshot.world.salvageTarget);
      const targetWorldY = this.activeMap.baseDepth + sampleMapHeight(this.activeMap, target.x, target.z);
      const relative = projectSalvageTargetToSonarLocal(snapshot.submarine, snapshot.world.salvageTarget);
      const localX = relative.x;
      const localZ = relative.z;
      this.waypointDot.visible = Math.hypot(localX, localZ) <= this.getSonarRadius();
      this.waypointDot.position.set(localX, targetWorldY - worldOriginY + 1.1, localZ);
    } else {
      this.waypointDot.visible = false;
    }
  }

  setRoomState(state: Pick<RoomState, 'mission'> & { difficulty?: Difficulty }): void {
    this.difficulty = state.difficulty ?? this.difficulty;
    const newMap = getMapByMissionId(state.mission);
    const mapChanged = newMap && (newMap.id !== this.activeMap.id || newMap.lastModified !== this.activeMap.lastModified);
    if (!newMap) {
      console.warn(`SonarScanView: failed to resolve map for mission ${state.mission}. Using existing activeMap ${this.activeMap.id}.`);
      return;
    }

    if (!mapChanged) {
      // The collision mesh may have loaded after this view was first initialised
      // (race: roleAssigned fires while renderRoomState is still awaiting the
      // collision-mesh fetch). Pick it up now without re-running the full reset.
      if (!this.collisionMesh && newMap.collisionMesh) {
        this.collisionMesh = this.normalizeCollisionMesh(newMap.collisionMesh, newMap.sourceGlbTileSizeMeters?.Y ?? null);
      }
      return;
    }

    this.activeMap = newMap;
    this.preloadModelBounds(this.activeMap);
    this.tileMeshSignature = '';
    this.tileMeshCheckX = Number.NaN;
    this.tileMeshCheckZ = Number.NaN;
    this.tileMeshCheckY = Number.NaN;
    this.collisionMesh = this.normalizeCollisionMesh(this.activeMap.collisionMesh, this.activeMap.sourceGlbTileSizeMeters?.Y ?? null);
    if (this.lastSnapshot) {
      this.applyLiveState(this.lastSnapshot);
      this.captureCombinedScan(this.lastSnapshot);
      this.updateDockingPointDots(this.lastSnapshot);
    }
  }

  dispose(): void {
    this.animationLoop?.stop();
    this.resizeObserver.disconnect();
    this.renderer.domElement.removeEventListener('touchstart', this.boundOnTouchStart);
    this.renderer.domElement.removeEventListener('touchmove', this.boundOnTouchMove);
    this.renderer.domElement.removeEventListener('mousedown', this.boundOnMouseDown);
    this.renderer.domElement.removeEventListener('mousemove', this.boundOnMouseMove);
    this.renderer.domElement.removeEventListener('mouseup', this.boundOnMouseUp);
    this.renderer.domElement.removeEventListener('wheel', this.boundOnWheel);
    document.removeEventListener('mouseup', this.boundOnDocumentMouseUp);
    document.removeEventListener('mouseleave', this.boundOnDocumentMouseUp);

    this.removeAllBubbleMeshes();
    disposeObject3D(this.scanPoints);
    disposeObject3D(this.resourcePoints);
    disposeObject3D(this.scanPulse);
    disposeObject3D(this.waypointDot);
    disposeObject3D(this.rangeRing);
    disposeObject3D(this.submarineMarker);
    this.scanPointGeometry.dispose();
    this.scanPointMaterial.dispose();
    this.resourcePointGeometry.dispose();
    this.resourcePointMaterial.dispose();
    this.bubbleGeometry.dispose();
    this.bubbleMaterialPrototype.dispose();
    this.renderer.dispose();
    this.container.replaceChildren();
  }

  private readonly boundOnTouchStart = (event: TouchEvent): void => this.onTouchStart(event);
  private readonly boundOnTouchMove = (event: TouchEvent): void => this.onTouchMove(event);
  private readonly boundOnMouseDown = (event: MouseEvent): void => this.onMouseDown(event);
  private readonly boundOnMouseMove = (event: MouseEvent): void => this.onMouseMove(event);
  private readonly boundOnMouseUp = (event: MouseEvent): void => this.onMouseUp(event);
  private readonly boundOnWheel = (event: WheelEvent): void => this.onWheel(event);
  private readonly boundOnDocumentMouseUp = (): void => this.onDocumentMouseUp();

  private removeAllBubbleMeshes(): void {
    for (const bubble of this.bubblePool) {
      this.thrusterBubbleGroup.remove(bubble.mesh);
      disposeMaterial(bubble.mesh.material as THREE.Material | THREE.Material[] | undefined);
    }
    this.bubblePool.length = 0;
  }

  private bindTouchControls(): void {
    const canvas = this.renderer.domElement;
    canvas.addEventListener('touchstart', this.boundOnTouchStart, { passive: true });
    canvas.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
    canvas.addEventListener('mousedown', this.boundOnMouseDown);
    canvas.addEventListener('mousemove', this.boundOnMouseMove);
    canvas.addEventListener('mouseup', this.boundOnMouseUp);
    canvas.addEventListener('wheel', this.boundOnWheel, { passive: false });
    document.addEventListener('mouseup', this.boundOnDocumentMouseUp);
    document.addEventListener('mouseleave', this.boundOnDocumentMouseUp);
  }

  private onTouchStart(event: TouchEvent): void {
    if (event.touches.length === 1) {
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
    } else if (event.touches.length === 2) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      this.touchPrevPinchDist = Math.hypot(dx, dy);
    }
  }

  private onTouchMove(event: TouchEvent): void {
    event.preventDefault();
    if (event.touches.length === 1) {
      const dx = event.touches[0].clientX - this.touchStartX;
      const dy = event.touches[0].clientY - this.touchStartY;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      // Drag sensitivity scaled to current distance so it feels consistent at all zoom levels
      const sensitivity = this.orbitRadius * 0.0058;
      this.orbitTheta -= dx * sensitivity * 0.045;
      this.orbitPhi = THREE.MathUtils.clamp(this.orbitPhi - dy * sensitivity * 0.045, this.orbitMinPhi, this.orbitMaxPhi);
    } else if (event.touches.length === 2) {
      const dx = event.touches[0].clientX - event.touches[1].clientX;
      const dy = event.touches[0].clientY - event.touches[1].clientY;
      const pinchDist = Math.hypot(dx, dy);
      const delta = this.touchPrevPinchDist - pinchDist;
      this.orbitRadius = THREE.MathUtils.clamp(this.orbitRadius + delta * 0.6, this.orbitMinRadius, this.orbitMaxRadius);
      this.touchPrevPinchDist = pinchDist;
    }
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button === 0) { // Left click only
      this.isMouseDown = true;
      this.isDragging = false;
      this.touchStartX = event.clientX;
      this.touchStartY = event.clientY;
      event.preventDefault();
    }
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isMouseDown) return;
    
    const dx = event.clientX - this.touchStartX;
    const dy = event.clientY - this.touchStartY;
    
    // Start dragging if moved enough to avoid accidental drags
    if (!this.isDragging && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
      this.isDragging = true;
    }
    
    if (this.isDragging) {
      this.touchStartX = event.clientX;
      this.touchStartY = event.clientY;
      // Drag sensitivity scaled to current distance so it feels consistent at all zoom levels
      const sensitivity = this.orbitRadius * 0.0058;
      this.orbitTheta -= dx * sensitivity * 0.045;
      this.orbitPhi = THREE.MathUtils.clamp(this.orbitPhi - dy * sensitivity * 0.045, this.orbitMinPhi, this.orbitMaxPhi);
      event.preventDefault();
    }
  }

  private onMouseUp(event: MouseEvent): void {
    if (event.button === 0) {
      this.isMouseDown = false;
      this.isDragging = false;
      event.preventDefault();
    }
  }

  private onDocumentMouseUp(): void {
    this.isMouseDown = false;
    this.isDragging = false;
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    // Normalize wheel delta for consistent zoom speed across browsers
    const delta = event.deltaY > 0 ? 1 : -1;
    const zoomSpeed = this.orbitRadius * 0.1; // Adaptive zoom speed based on current distance
    this.orbitRadius = THREE.MathUtils.clamp(this.orbitRadius + delta * zoomSpeed, this.orbitMinRadius, this.orbitMaxRadius);
  }

  private buildRayDirections(pointCount: number): THREE.Vector3[] {
    const directions: THREE.Vector3[] = [];
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    for (let index = 0; index < pointCount; index += 1) {
      const ratio = (index + 0.5) / pointCount;
      // Full-sphere Fibonacci lattice: polar ranges 0 → PI covering every direction.
      const polar = Math.acos(1 - 2 * ratio);
      const azimuth = index * goldenAngle;
      const sinPolar = Math.sin(polar);
      const cosPolar = Math.cos(polar);

      directions.push(new THREE.Vector3(Math.cos(azimuth) * sinPolar, cosPolar, Math.sin(azimuth) * sinPolar));
    }

    return directions;
  }

  private projectCompassLabels(): void {
    this.camera.updateMatrixWorld();
    for (const key of Object.keys(this.compassWorldPositions) as Array<keyof typeof this.compassWorldPositions>) {
      const ndc = this.compassWorldPositions[key].clone().project(this.camera);
      const label = this.compassLabels[key];
      if (ndc.z > 1) {
        // Behind the camera — hide
        label.style.opacity = '0';
      } else {
        const left = ((ndc.x + 1) / 2) * 100;
        const top = ((-ndc.y + 1) / 2) * 100;
        label.style.left = `${left}%`;
        label.style.top = `${top}%`;
        label.style.opacity = key === 'N' ? '1' : '0.82';
      }
    }
  }

  private async loadSubmarineGlbModel(): Promise<void> {
    const modelPath = '/models/submarine_low.glb';
    try {
      const model = await loadGlbModel(modelPath);

      while (this.submarineMarker.children.length > 0) {
        const child = this.submarineMarker.children[0];
        this.submarineMarker.remove(child);
        disposeObject3D(child);
      }

      // The GLB's native bow points along -X; the marker group's convention is bow = -Z (so a plain heading yaw
      // orients it). Same quarter turn WorldViewportSubmarine applies, minus the PI offset that flipped it astern.
      model.rotation.set(0, -Math.PI / 2, 0);
      model.updateMatrixWorld(true);
      const bbox = new THREE.Box3().setFromObject(model);
      const size = bbox.getSize(new THREE.Vector3());
      const maxSize = Math.max(size.x, size.y, size.z, 1);
      const scale = 4.2 / maxSize;
      model.position.sub(bbox.getCenter(new THREE.Vector3()).multiplyScalar(scale));
      model.scale.setScalar(scale);

      this.submarineMarker.add(model);
    } catch (error) {
      console.error('Failed to load submarine GLB for sonar view:', modelPath, error);
    }
  }

  private buildSubmarineMarker(): void {
    const hullMat = new THREE.MeshStandardMaterial({ color: 0xa4f7ff, transparent: true, opacity: 0.88, roughness: 0.38, metalness: 0.62 });
    const accentMat = new THREE.MeshStandardMaterial({ color: 0xdcfeff, transparent: true, opacity: 0.92, roughness: 0.28, metalness: 0.7 });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x5ab8cc, transparent: true, opacity: 0.82, roughness: 0.5, metalness: 0.5 });
    const bladeMat = new THREE.MeshStandardMaterial({ color: 0x7fd6e6, transparent: true, opacity: 0.72, side: THREE.DoubleSide, roughness: 0.4, metalness: 0.6 });

    // Main pressure hull
    const hull = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 5.5, 18), hullMat);
    hull.rotation.x = Math.PI / 2;

    // Bow dome (forward hemisphere)
    const bow = new THREE.Mesh(new THREE.SphereGeometry(0.9, 16, 10, 0, Math.PI * 2, 0, Math.PI / 2), hullMat);
    bow.rotation.x = -Math.PI / 2;
    bow.position.z = -2.75;

    // Stern taper
    const stern = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.9, 1.2, 14), darkMat);
    stern.rotation.x = Math.PI / 2;
    stern.position.z = 3.35;

    // Conning tower (sail)
    const sailBody = new THREE.Mesh(new THREE.BoxGeometry(0.62, 1.15, 1.85), hullMat);
    sailBody.position.set(0, 0.9 + 0.575, -0.3);
    const sailTop = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.31, 0.2, 10), accentMat);
    sailTop.position.set(0, 0.9 + 1.15 + 0.1, -0.3);

    // Periscope masts
    for (const xOff of [-0.15, 0.15]) {
      const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.85, 6), accentMat);
      mast.position.set(xOff, 0.9 + 1.15 + 0.2 + 0.425, -0.3);
      this.submarineMarker.add(mast);
    }

    // Forward dive planes (across hull, mid-position)
    const fwdPlane = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.065, 0.44), accentMat);
    fwdPlane.position.set(0, 0, -0.8);

    // Aft stern planes
    const aftPlane = new THREE.Mesh(new THREE.BoxGeometry(2.3, 0.065, 0.36), accentMat);
    aftPlane.position.set(0, 0, 2.3);

    // Vertical rudder
    const rudder = new THREE.Mesh(new THREE.BoxGeometry(0.065, 1.05, 0.4), accentMat);
    rudder.position.set(0, 0.22, 2.55);

    // Propeller hub
    const propHub = new THREE.Mesh(new THREE.IcosahedronGeometry(0.24, 1), darkMat);
    propHub.position.z = 4.0;

    // Prop blades (4)
    for (let i = 0; i < 4; i++) {
      const blade = new THREE.Mesh(new THREE.PlaneGeometry(0.38, 0.95), bladeMat);
      blade.rotation.z = (i / 4) * Math.PI * 2;
      blade.position.z = 4.05;
      this.submarineMarker.add(blade);
    }

    // Thruster pods (port and starboard)
    for (const xOff of [-0.88, 0.88]) {
      const pod = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.85, 10), darkMat);
      pod.rotation.x = Math.PI / 2;
      pod.position.set(xOff, -0.6, 1.65);
      this.submarineMarker.add(pod);
    }

    this.submarineMarker.add(hull, bow, stern, sailBody, sailTop, fwdPlane, aftPlane, rudder, propHub);
  }

  private buildObstacles(originY: number): SonarObstacle[] {
    const obs: SonarObstacle[] = [];

    for (const vent of this.activeMap.hydrothermalVents) {
      const baseY = this.activeMap.baseDepth + sampleMapHeight(this.activeMap, vent.x, vent.z);
      // Approximate each vent as a tapered stack so sonar reads it like a cone plume source.
      const ventHeight = 14;
      const baseRadius = vent.radius * 5;
      const segmentCount = 5;
      for (let segment = 0; segment < segmentCount; segment += 1) {
        const t0 = segment / segmentCount;
        const t1 = (segment + 1) / segmentCount;
        const yMin = baseY + ventHeight * t0;
        const yMax = baseY + ventHeight * t1;
        const radius = THREE.MathUtils.lerp(baseRadius, baseRadius * 0.2, (t0 + t1) * 0.5);
        obs.push({ kind: 'cylinder', cx: vent.x, cz: vent.z, radius, yMin, yMax, cr: 0.95, cg: 0.42, cb: 0.08 });
      }
    }

    // Seaweed is no longer rendered in the world, so it must not scan either (it used to appear as green columns).

    for (const collisionVolume of this.activeMap.collisionVolumes ?? []) {
      obs.push({
        kind: 'horizontalCylinder',
        axis: collisionVolume.axis,
        cx: collisionVolume.centerX,
        cz: collisionVolume.centerZ,
        radius: collisionVolume.radius,
        halfLength: collisionVolume.length / 2,
        cr: 0.64,
        cg: 0.86,
        cb: 1.0
      });
    }

    for (const marker of this.activeMap.glbModelMarkers ?? []) {
      obs.push(...this.buildModelMarkerObstacles(marker));
    }

    return obs;
  }

  private ensureModelBounds(modelKey: string): void {
    if (this.modelVoxels.has(modelKey) || this.modelBoundsLoading.has(modelKey)) {
      return;
    }

    const promise = loadModelVoxels(modelKey)
      .then((voxels) => {
        this.modelVoxels.set(modelKey, voxels);
      })
      .catch((error) => {
        // Placeholder markers (seaweed, vents, sounds...) have no model file; they are not sonar targets.
        this.modelVoxels.set(modelKey, { cell: 1, cells: new Int16Array(0) });
        console.debug(`No sonar geometry for marker model: ${modelKey}`, error);
      })
      .finally(() => {
        this.modelBoundsLoading.delete(modelKey);
      });

    this.modelBoundsLoading.set(modelKey, promise);
  }

  private static isInvisibleMarker(model: string): boolean {
    return model.toLowerCase() === 'nav_point';
  }

  private preloadModelBounds(map: MapDefinition): void {
    for (const marker of map.glbModelMarkers ?? []) {
      if (!SonarScanView.isInvisibleMarker(marker.model)) {
        this.ensureModelBounds(marker.model);
      }
    }
  }

  private buildModelMarkerObstacles(marker: GlbModelMarker): SonarObstacle[] {
    if (SonarScanView.isInvisibleMarker(marker.model) || SonarScanView.isDecorationMarker(marker.model)) return [];
    this.ensureModelBounds(marker.model);
    const voxels = this.modelVoxels.get(marker.model);
    if (!voxels || voxels.cells.length === 0) {
      return [];
    }

    const baseY = -marker.depth;
    const cosYaw = Math.cos(marker.yaw);
    const sinYaw = Math.sin(marker.yaw);
    const cell = voxels.cell * marker.scale;
    // A cube turned by yaw spans this much along each world axis.
    const half = cell * 0.5 * (Math.abs(cosYaw) + Math.abs(sinYaw));
    const count = voxels.cells.length / 3;
    const cubes = new Float32Array(count * 3);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (let i = 0; i < count; i += 1) {
      const localX = (voxels.cells[i * 3] + 0.5) * cell;
      const localZ = (voxels.cells[i * 3 + 2] + 0.5) * cell;
      const x = marker.mapX + localX * cosYaw - localZ * sinYaw;
      const z = marker.mapZ + localX * sinYaw + localZ * cosYaw;
      const y = baseY + (voxels.cells[i * 3 + 1] + 0.5) * cell;
      cubes[i * 3] = x; cubes[i * 3 + 1] = y; cubes[i * 3 + 2] = z;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    return [{
      kind: 'voxels',
      cx: (minX + maxX) / 2,
      cz: (minZ + maxZ) / 2,
      halfWidth: (maxX - minX) / 2 + half,
      halfDepth: (maxZ - minZ) / 2 + half,
      yMin: minY - half,
      yMax: maxY + half,
      cubes,
      half,
      cr: 0.68, cg: 0.78, cb: 0.92
    }];
  }

  /** Small scenery whose model is not a sonar target (it was never traced before either). */
  private static isDecorationMarker(model: string): boolean {
    const name = model.toLowerCase();
    return name === 'coral' || name === 'lava_rock' || name === 'seaweed';
  }

  private isectVoxels(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    obs: Extract<SonarObstacle, { kind: 'voxels' }>
  ): number | null {
    if (this.isectBox(ox, oy, oz, dx, dy, dz, obs.cx, obs.cz, obs.halfWidth, obs.halfDepth, obs.yMin, obs.yMax) === null) {
      return null;
    }
    let best: number | null = null;
    const { cubes, half } = obs;
    for (let i = 0; i < cubes.length; i += 3) {
      const t = this.isectBox(ox, oy, oz, dx, dy, dz, cubes[i], cubes[i + 2], half, half, cubes[i + 1] - half, cubes[i + 1] + half);
      if (t !== null && (best === null || t < best)) best = t;
    }
    return best;
  }

  private checkObjectHit(
    ox: number, oy: number, oz: number,
    dir: THREE.Vector3,
    obstacles: SonarObstacle[]
  ): { distance: number; r: number; g: number; b: number } | null {
    let best: { distance: number; r: number; g: number; b: number } | null = null;
    for (const obs of obstacles) {
      const t = obs.kind === 'sphere'
        ? this.isectSphere(ox, oy, oz, dir.x, dir.y, dir.z, obs.cx, obs.cy, obs.cz, obs.radius)
        : obs.kind === 'cylinder'
          ? this.isectVertCylinder(ox, oy, oz, dir.x, dir.y, dir.z, obs.cx, obs.cz, obs.radius, obs.yMin, obs.yMax)
          : obs.kind === 'voxels'
            ? this.isectVoxels(ox, oy, oz, dir.x, dir.y, dir.z, obs)
          : obs.kind === 'horizontalCylinder'
            ? this.isectHorizontalCylinder(ox, oy, oz, dir.x, dir.y, dir.z, obs.axis, obs.cx, obs.cz, obs.radius, obs.halfLength)
            : this.isectBox(ox, oy, oz, dir.x, dir.y, dir.z, obs.cx, obs.cz, obs.halfWidth, obs.halfDepth, obs.yMin, obs.yMax);
      if (t !== null && t > 0.5 && t <= this.getSonarRadius() && (best === null || t < best.distance)) {
        best = { distance: t, r: obs.cr, g: obs.cg, b: obs.cb };
      }
    }
    return best;
  }

  /** Intersect a ray with a sphere. Returns closest positive t, or null. */
  private isectSphere(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    cx: number, cy: number, cz: number,
    radius: number
  ): number | null {
    const lx = ox - cx;
    const ly = oy - cy;
    const lz = oz - cz;
    const a = dx * dx + dy * dy + dz * dz;
    const b = 2 * (lx * dx + ly * dy + lz * dz);
    const c = lx * lx + ly * ly + lz * lz - radius * radius;
    const disc = b * b - 4 * a * c;
    if (disc < 0) {
      return null;
    }

    const sqrtD = Math.sqrt(disc);
    const t0 = (-b - sqrtD) / (2 * a);
    const t1 = (-b + sqrtD) / (2 * a);
    const near = Math.min(t0, t1);
    const far = Math.max(t0, t1);

    if (near > 0.5) {
      return near;
    }
    if (far > 0.5) {
      return far;
    }
    return null;
  }

  /**
   * Intersect a ray with a capped vertical cylinder. Returns the closest positive t, or null.
   * The end caps matter: without them a ray entering through the top skips the near wall (it is above
   * the rim) and reports the INSIDE of the far wall, so every seaweed cluster and vent segment scanned
   * from above rendered as an open, flared cone instead of a solid column.
   */
  private isectVertCylinder(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    cx: number, cz: number,
    radius: number, yMin: number, yMax: number
  ): number | null {
    const MIN_T = 0.5;
    let best: number | null = null;
    const consider = (t: number): void => {
      if (t >= MIN_T && (best === null || t < best)) best = t;
    };

    // Side wall.
    const ax = ox - cx;
    const az = oz - cz;
    const a = dx * dx + dz * dz;
    if (a > 1e-12) {
      const b = 2 * (ax * dx + az * dz);
      const c = ax * ax + az * az - radius * radius;
      const disc = b * b - 4 * a * c;
      if (disc >= 0) {
        const sqrtD = Math.sqrt(disc);
        for (const t of [(-b - sqrtD) / (2 * a), (-b + sqrtD) / (2 * a)]) {
          const yt = oy + dy * t;
          if (yt >= yMin && yt <= yMax) consider(t);
        }
      }
    }

    // End caps.
    if (Math.abs(dy) > 1e-9) {
      const radiusSq = radius * radius;
      for (const capY of [yMin, yMax]) {
        const t = (capY - oy) / dy;
        const hx = ax + dx * t;
        const hz = az + dz * t;
        if (hx * hx + hz * hz <= radiusSq) consider(t);
      }
    }
    return best;
  }

  private isectHorizontalCylinder(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    axis: 'x' | 'z',
    cx: number, cz: number,
    radius: number,
    halfLength: number
  ): number | null {
    const centerY = oy;
    let a: number;
    let b: number;
    let c: number;
    let tCandidates: number[] = [];

    if (axis === 'x') {
      const ay = oy - centerY;
      const az0 = oz - cz;
      a = dy * dy + dz * dz;
      if (a === 0) return null;
      b = 2 * (ay * dy + az0 * dz);
      c = ay * ay + az0 * az0 - radius * radius;
      const disc = b * b - 4 * a * c;
      if (disc < 0) return null;
      const sqrtD = Math.sqrt(disc);
      tCandidates = [(-b - sqrtD) / (2 * a), (-b + sqrtD) / (2 * a)];
      for (const t of tCandidates) {
        if (t < 0.5) continue;
        const xHit = ox + dx * t;
        if (xHit >= cx - halfLength && xHit <= cx + halfLength) {
          return t;
        }
      }
    } else {
      const ax = ox - cx;
      const ay = oy - centerY;
      a = dx * dx + dy * dy;
      if (a === 0) return null;
      b = 2 * (ax * dx + ay * dy);
      c = ax * ax + ay * ay - radius * radius;
      const disc = b * b - 4 * a * c;
      if (disc < 0) return null;
      const sqrtD = Math.sqrt(disc);
      tCandidates = [(-b - sqrtD) / (2 * a), (-b + sqrtD) / (2 * a)];
      for (const t of tCandidates) {
        if (t < 0.5) continue;
        const zHit = oz + dz * t;
        if (zHit >= cz - halfLength && zHit <= cz + halfLength) {
          return t;
        }
      }
    }

    return null;
  }

  private isectBox(
    ox: number, oy: number, oz: number,
    dx: number, dy: number, dz: number,
    cx: number, cz: number,
    halfWidth: number, halfDepth: number,
    yMin: number, yMax: number
  ): number | null {
    const xMin = cx - halfWidth;
    const xMax = cx + halfWidth;
    const zMin = cz - halfDepth;
    const zMax = cz + halfDepth;

    const axisSlab = (origin: number, direction: number, min: number, max: number) => {
      if (Math.abs(direction) < 1e-8) {
        return origin < min || origin > max ? null : { tMin: -Infinity, tMax: Infinity };
      }
      const t1 = (min - origin) / direction;
      const t2 = (max - origin) / direction;
      return { tMin: Math.min(t1, t2), tMax: Math.max(t1, t2) };
    };

    const xSlab = axisSlab(ox, dx, xMin, xMax);
    const zSlab = axisSlab(oz, dz, zMin, zMax);
    const ySlab = axisSlab(oy, dy, yMin, yMax);
    if (!xSlab || !zSlab || !ySlab) {
      return null;
    }

    const tMin = Math.max(xSlab.tMin, zSlab.tMin, ySlab.tMin);
    const tMax = Math.min(xSlab.tMax, zSlab.tMax, ySlab.tMax);
    if (tMax < tMin) {
      return null;
    }

    if (tMin > 0.5) {
      return tMin;
    }
    if (tMax > 0.5) {
      return tMax;
    }
    return null;
  }

  private findFirstHitDistance(
    originX: number,
    originY: number,
    originZ: number,
    worldDirection: THREE.Vector3
  ): number | null {
    const originDelta = originY - this.sampleFloorWorldY(originX, originZ);
    if (originDelta <= 0) {
      return 0;
    }

    const sonarRadius = this.getSonarRadius();
    const iterations = Math.ceil(sonarRadius / SONAR_RAY_STEP_METERS);
    let previousDistance = 0;

    for (let i = 1; i <= iterations; i += 1) {
      const distance = (i / iterations) * sonarRadius;
      const sampleX = originX + worldDirection.x * distance;
      const sampleY = originY + worldDirection.y * distance;
      const sampleZ = originZ + worldDirection.z * distance;
      const delta = sampleY - this.sampleFloorWorldY(sampleX, sampleZ);

      if (delta <= 0) {
        let nearDistance = previousDistance;
        let farDistance = distance;

        for (let refinement = 0; refinement < 4; refinement += 1) {
          const midDistance = (nearDistance + farDistance) * 0.5;
          const midX = originX + worldDirection.x * midDistance;
          const midY = originY + worldDirection.y * midDistance;
          const midZ = originZ + worldDirection.z * midDistance;
          const midDelta = midY - this.sampleFloorWorldY(midX, midZ);

          if (midDelta > 0) {
            nearDistance = midDistance;
          } else {
            farDistance = midDistance;
          }
        }

        return farDistance;
      }

      previousDistance = distance;
    }

    return null;
  }

  private normalizeCollisionMesh(mesh: CollisionMesh | undefined, tileY: number | null): NormalizedCollisionMesh | null {
    if (!mesh) {
      return null;
    }

    const positions = mesh.positions instanceof Float32Array
      ? mesh.positions
      : new Float32Array(mesh.positions);
    const indices = mesh.indices instanceof Uint32Array
      ? mesh.indices
      : new Uint32Array(mesh.indices);

    const triangleCount = Math.floor(indices.length / 3);
    if (triangleCount === 0) {
      return null;
    }

    const triangleIndices: number[] = new Array(triangleCount);
    const triangleAABBs = new Float32Array(triangleCount * 6);
    const triangleCentroids = new Float32Array(triangleCount * 3);

    for (let tri = 0; tri < triangleCount; tri += 1) {
      triangleIndices[tri] = tri;
      const i0 = indices[tri * 3] * 3;
      const i1 = indices[tri * 3 + 1] * 3;
      const i2 = indices[tri * 3 + 2] * 3;

      const ax = positions[i0];
      const ay = positions[i0 + 1];
      const az = positions[i0 + 2];
      const bx = positions[i1];
      const by = positions[i1 + 1];
      const bz = positions[i1 + 2];
      const cx = positions[i2];
      const cy = positions[i2 + 1];
      const cz = positions[i2 + 2];

      const minX = Math.min(ax, bx, cx);
      const minY = Math.min(ay, by, cy);
      const minZ = Math.min(az, bz, cz);
      const maxX = Math.max(ax, bx, cx);
      const maxY = Math.max(ay, by, cy);
      const maxZ = Math.max(az, bz, cz);

      triangleAABBs[tri * 6] = minX;
      triangleAABBs[tri * 6 + 1] = minY;
      triangleAABBs[tri * 6 + 2] = minZ;
      triangleAABBs[tri * 6 + 3] = maxX;
      triangleAABBs[tri * 6 + 4] = maxY;
      triangleAABBs[tri * 6 + 5] = maxZ;

      triangleCentroids[tri * 3] = (ax + bx + cx) / 3;
      triangleCentroids[tri * 3 + 1] = (ay + by + cy) / 3;
      triangleCentroids[tri * 3 + 2] = (az + bz + cz) / 3;
    }

    let minY = Infinity;
    for (let i = 1; i < positions.length; i += 3) {
      if (positions[i] < minY) minY = positions[i];
    }

    const bvhNodes: CollisionMeshBVHNode[] = [];
    this.buildCollisionMeshBVH(triangleIndices, triangleAABBs, triangleCentroids, bvhNodes, 0, triangleCount);

    return {
      positions,
      indices,
      topY: mesh.topY,
      minY,
      tileY,
      bvhNodes,
      triangleIndices,
      triangleAABBs,
      triangleCentroids
    };
  }

  private buildCollisionMeshBVH(
    triangleIndices: number[],
    triangleAABBs: Float32Array,
    triangleCentroids: Float32Array,
    nodes: CollisionMeshBVHNode[],
    start: number,
    count: number
  ): number {
    const nodeIndex = nodes.length;
    nodes.push({
      minX: Infinity,
      minY: Infinity,
      minZ: Infinity,
      maxX: -Infinity,
      maxY: -Infinity,
      maxZ: -Infinity,
      start,
      count,
      left: null,
      right: null
    });

    const end = start + count;
    if (count <= 16) {
      for (let i = start; i < end; i += 1) {
        const triIndex = triangleIndices[i] * 6;
        nodes[nodeIndex].minX = Math.min(nodes[nodeIndex].minX, triangleAABBs[triIndex]);
        nodes[nodeIndex].minY = Math.min(nodes[nodeIndex].minY, triangleAABBs[triIndex + 1]);
        nodes[nodeIndex].minZ = Math.min(nodes[nodeIndex].minZ, triangleAABBs[triIndex + 2]);
        nodes[nodeIndex].maxX = Math.max(nodes[nodeIndex].maxX, triangleAABBs[triIndex + 3]);
        nodes[nodeIndex].maxY = Math.max(nodes[nodeIndex].maxY, triangleAABBs[triIndex + 4]);
        nodes[nodeIndex].maxZ = Math.max(nodes[nodeIndex].maxZ, triangleAABBs[triIndex + 5]);
      }
      return nodeIndex;
    }

    let minCX = Infinity;
    let minCY = Infinity;
    let minCZ = Infinity;
    let maxCX = -Infinity;
    let maxCY = -Infinity;
    let maxCZ = -Infinity;

    for (let i = start; i < end; i += 1) {
      const triIndex = triangleIndices[i] * 3;
      const cx = triangleCentroids[triIndex];
      const cy = triangleCentroids[triIndex + 1];
      const cz = triangleCentroids[triIndex + 2];
      minCX = Math.min(minCX, cx);
      minCY = Math.min(minCY, cy);
      minCZ = Math.min(minCZ, cz);
      maxCX = Math.max(maxCX, cx);
      maxCY = Math.max(maxCY, cy);
      maxCZ = Math.max(maxCZ, cz);
    }

    const xExtent = maxCX - minCX;
    const yExtent = maxCY - minCY;
    const zExtent = maxCZ - minCZ;
    const axis = xExtent >= yExtent && xExtent >= zExtent ? 0 : yExtent >= zExtent ? 1 : 2;

    // Median split by selection (O(n)) rather than a full sort per level: the sort made a 30k-triangle sewer merge take
    // ~0.7 s on a phone's main thread every time the loaded tile set changed.
    const mid = start + (count >> 1);
    selectByCentroid(triangleIndices, triangleCentroids, axis, start, end, mid);
    const leftIndex = this.buildCollisionMeshBVH(triangleIndices, triangleAABBs, triangleCentroids, nodes, start, mid - start);
    const rightIndex = this.buildCollisionMeshBVH(triangleIndices, triangleAABBs, triangleCentroids, nodes, mid, end - mid);

    const left = nodes[leftIndex];
    const right = nodes[rightIndex];

    nodes[nodeIndex].left = leftIndex;
    nodes[nodeIndex].right = rightIndex;
    nodes[nodeIndex].count = 0;
    nodes[nodeIndex].start = 0;
    nodes[nodeIndex].minX = Math.min(left.minX, right.minX);
    nodes[nodeIndex].minY = Math.min(left.minY, right.minY);
    nodes[nodeIndex].minZ = Math.min(left.minZ, right.minZ);
    nodes[nodeIndex].maxX = Math.max(left.maxX, right.maxX);
    nodes[nodeIndex].maxY = Math.max(left.maxY, right.maxY);
    nodes[nodeIndex].maxZ = Math.max(left.maxZ, right.maxZ);

    return nodeIndex;
  }

  private findCollisionMeshHitDistance(
    originX: number,
    originY: number,
    originZ: number,
    worldDirection: THREE.Vector3,
    mesh: NormalizedCollisionMesh
  ): number | null {
    const { positions, indices, bvhNodes, triangleIndices } = mesh;
    if (bvhNodes.length === 0) {
      return null;
    }

    const maxDistance = this.getSonarRadius();
    let closest: number | null = null;
    const stack: number[] = [0];

    while (stack.length > 0) {
      const node = bvhNodes[stack.pop()!];
      if (!this.rayIntersectsAABB(
        originX,
        originY,
        originZ,
        worldDirection.x,
        worldDirection.y,
        worldDirection.z,
        node.minX,
        node.minY,
        node.minZ,
        node.maxX,
        node.maxY,
        node.maxZ,
        closest === null ? maxDistance : Math.min(maxDistance, closest)
      )) {
        continue;
      }

      if (node.left !== null && node.right !== null) {
        stack.push(node.right);
        stack.push(node.left);
        continue;
      }

      const endIndex = node.start + node.count;
      for (let i = node.start; i < endIndex; i += 1) {
        const tri = triangleIndices[i] * 3;
        const ai = indices[tri] * 3;
        const bi = indices[tri + 1] * 3;
        const ci = indices[tri + 2] * 3;

        const aX = positions[ai];
        const aY = positions[ai + 1];
        const aZ = positions[ai + 2];
        const bX = positions[bi];
        const bY = positions[bi + 1];
        const bZ = positions[bi + 2];
        const cX = positions[ci];
        const cY = positions[ci + 1];
        const cZ = positions[ci + 2];

        if (this.isTileBoundaryCap(aY, bY, cY, mesh)) continue;

        const t = this.rayIntersectTriangle(
          originX,
          originY,
          originZ,
          worldDirection.x,
          worldDirection.y,
          worldDirection.z,
          aX,
          aY,
          aZ,
          bX,
          bY,
          bZ,
          cX,
          cY,
          cZ
        );

        if (t !== null && t >= 0.5 && t <= maxDistance && (closest === null || t < closest)) {
          closest = t;
        }
      }
    }

    return closest;
  }

  private rayIntersectsAABB(
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    minX: number,
    minY: number,
    minZ: number,
    maxX: number,
    maxY: number,
    maxZ: number,
    maxDistance: number
  ): boolean {
    let tMin = 0.5;
    let tMax = maxDistance;

    const testAxis = (origin: number, direction: number, min: number, max: number): boolean => {
      if (Math.abs(direction) < 1e-8) {
        return origin >= min && origin <= max;
      }
      const invDir = 1 / direction;
      let t1 = (min - origin) * invDir;
      let t2 = (max - origin) * invDir;
      if (t1 > t2) {
        const temp = t1;
        t1 = t2;
        t2 = temp;
      }
      tMin = Math.max(tMin, t1);
      tMax = Math.min(tMax, t2);
      return tMax >= tMin;
    };

    if (!testAxis(ox, dx, minX, maxX) || !testAxis(oy, dy, minY, maxY) || !testAxis(oz, dz, minZ, maxZ)) {
      return false;
    }

    return tMax >= tMin;
  }

  private findCollisionVolumeHitDistance(
    originX: number,
    originY: number,
    originZ: number,
    worldDirection: THREE.Vector3
  ): number | null {
    if (!this.activeMap.collisionVolumes?.length) {
      return null;
    }

    let closest: number | null = null;

    for (const volume of this.activeMap.collisionVolumes) {
      const axisOrigin = volume.axis === 'x' ? originX : originZ;
      const axisDirection = volume.axis === 'x' ? worldDirection.x : worldDirection.z;
      const crossOrigin = volume.axis === 'x' ? originZ : originX;
      const crossDirection = volume.axis === 'x' ? worldDirection.z : worldDirection.x;
      const axisCenter = volume.axis === 'x' ? volume.centerX : volume.centerZ;
      const crossCenter = volume.axis === 'x' ? volume.centerZ : volume.centerX;
      const halfLength = volume.length / 2;

      const a = crossDirection * crossDirection;
      const b = 2 * (crossOrigin - crossCenter) * crossDirection;
      const c = (crossOrigin - crossCenter) * (crossOrigin - crossCenter) - volume.radius * volume.radius;
      const candidates: number[] = [];

      if (Math.abs(a) < 1e-8) {
        if (c <= 0 && Math.abs(axisDirection) > 1e-8) {
          candidates.push((axisCenter - halfLength - axisOrigin) / axisDirection);
          candidates.push((axisCenter + halfLength - axisOrigin) / axisDirection);
        }
      } else {
        const discriminant = b * b - 4 * a * c;
        if (discriminant < 0) {
          continue;
        }

        const sqrtDisc = Math.sqrt(discriminant);
        candidates.push((-b - sqrtDisc) / (2 * a), (-b + sqrtDisc) / (2 * a));
      }

      for (const candidate of candidates) {
        if (candidate < 0.5) {
          continue;
        }

        const axisAt = axisOrigin + axisDirection * candidate;
        if (axisAt < axisCenter - halfLength || axisAt > axisCenter + halfLength) {
          continue;
        }

        if (closest === null || candidate < closest) {
          closest = candidate;
        }
      }
    }

    return closest;
  }

  private rayIntersectTriangle(
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    ax: number,
    ay: number,
    az: number,
    bx: number,
    by: number,
    bz: number,
    cx: number,
    cy: number,
    cz: number
  ): number | null {
    const abx = bx - ax;
    const aby = by - ay;
    const abz = bz - az;
    const acx = cx - ax;
    const acy = cy - ay;
    const acz = cz - az;

    const px = dy * acz - dz * acy;
    const py = dz * acx - dx * acz;
    const pz = dx * acy - dy * acx;

    const det = abx * px + aby * py + abz * pz;
    if (Math.abs(det) < 1e-8) {
      return null;
    }

    const invDet = 1 / det;
    const oxax = ox - ax;
    const oyay = oy - ay;
    const ozaz = oz - az;
    const u = (oxax * px + oyay * py + ozaz * pz) * invDet;
    if (u < 0 || u > 1) {
      return null;
    }

    const qx = oyay * abz - ozaz * aby;
    const qy = ozaz * abx - oxax * abz;
    const qz = oxax * aby - oyay * abx;
    const v = (dx * qx + dy * qy + dz * qz) * invDet;
    if (v < 0 || u + v > 1) {
      return null;
    }

    const t = (acx * qx + acy * qy + acz * qz) * invDet;
    return t > 0 ? t : null;
  }

  /** Returns true for near-horizontal triangles whose Y sits on a tile boundary (cap faces). */
  private isTileBoundaryCap(
    ay: number, by: number, cy: number,
    mesh: NormalizedCollisionMesh
  ): boolean {
    if (!mesh.tileY) return false;
    const EPS = 0.15;
    const triMinY = Math.min(ay, by, cy);
    const triMaxY = Math.max(ay, by, cy);
    if (triMaxY - triMinY > EPS) return false; // not flat
    const y = (triMinY + triMaxY) * 0.5;
    const offset = y - mesh.minY;
    const remainder = ((offset % mesh.tileY) + mesh.tileY) % mesh.tileY;
    return remainder < EPS || remainder > mesh.tileY - EPS;
  }

  private sampleFloorWorldY(worldX: number, worldZ: number): number {
    return this.activeMap.baseDepth + sampleMapHeight(this.activeMap, worldX, worldZ);
  }

  private resize(): void {
    const width = Math.max(180, Math.floor(this.container.clientWidth));
    const height = Math.max(180, Math.floor(this.container.clientHeight));
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
  }

  private animate(timeMs: number): void {
    const age = this.pingCapturedAt > 0 ? timeMs - this.pingCapturedAt : 0;
    const fade = this.pingCapturedAt > 0 ? sonarPingFade(age / 1000, this.difficulty) : 1;

    const pulseMaterial = this.scanPulse.material as THREE.MeshBasicMaterial;
    const waypointMaterial = this.waypointDot.material as THREE.MeshBasicMaterial;
    const rangeRingMaterial = this.rangeRing.material as THREE.MeshBasicMaterial;
    const pulseWave = (Math.sin(timeMs * 0.0044) + 1) * 0.5;
    this.scanPulse.scale.setScalar(1 + pulseWave * 4.4);

    // Reveal points in distance order: a hit 100m away appears after 1 second
    const elapsedMs = this.pingCapturedAt > 0 ? timeMs - this.pingCapturedAt : 0;
    const maxRevealDistance = (elapsedMs / 1000) * this.getSonarRadius();
    let lo = 0;
    let hi = this.frozenHitCount;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.scanPointDistances[mid] <= maxRevealDistance) {
        lo = mid + 1;
      } else {
        hi = mid;
      }
    }
    this.scanPointGeometry.setDrawRange(0, lo);
    this.scanPointMaterial.opacity = fade;

    let resourceLo = 0;
    let resourceHi = this.resourceFrozenHitCount;
    while (resourceLo < resourceHi) {
      const mid = (resourceLo + resourceHi) >> 1;
      if (this.resourcePointDistances[mid] <= maxRevealDistance) {
        resourceLo = mid + 1;
      } else {
        resourceHi = mid;
      }
    }
    this.resourcePointGeometry.setDrawRange(0, resourceLo);
    this.resourcePointMaterial.opacity = fade;

    pulseMaterial.opacity = (0.08 + pulseWave * 0.2) * fade;
    waypointMaterial.opacity = this.waypointDot.visible ? (0.62 + pulseWave * 0.32) * fade : 0;
    this.waypointDot.scale.setScalar(1.08 + pulseWave * 0.34);
    rangeRingMaterial.opacity = 0.16;

    // Update camera from spherical orbit state every frame
    const camX = this.orbitRadius * Math.sin(this.orbitPhi) * Math.sin(this.orbitTheta);
    const camY = this.orbitRadius * Math.cos(this.orbitPhi);
    const camZ = this.orbitRadius * Math.sin(this.orbitPhi) * Math.cos(this.orbitTheta);
    this.camera.position.set(camX, camY, camZ);
    this.camera.lookAt(0, 0, 0);

    this.projectCompassLabels();
    this.updateThrusterBubbles(timeMs);

    this.renderer.render(this.scene, this.camera);
  }

  private updateThrusterBubbles(timeMs: number): void {
    const sub = this.lastSnapshot?.submarine;
    const speed = sub ? Math.abs(sub.speed) + Math.abs(sub.lateralSpeed) : 0;
    const dt = 0.016; // approximate frame delta
    const MAX_BUBBLES = 120;
    const BUBBLES_PER_THRUSTER = 5;

    // Emit new bubbles when moving
    const MIN_EMIT_MS = speed > 0.05 ? Math.max(120, 600 / (speed + 0.1)) : 99999;
    if (speed > 0.05 && this.bubblePool.length < MAX_BUBBLES && timeMs - this.lastBubbleEmitMs > MIN_EMIT_MS) {
      this.lastBubbleEmitMs = timeMs;
      const cosY = Math.cos(this.currentYawRad);
      const sinY = Math.sin(this.currentYawRad);
      // Forward direction in local space is -Z, so bubbles eject opposite to that vector.
      const backwardX = sinY;
      const backwardZ = cosY;
      for (const localX of [-0.88, 0.88]) {
        // Thruster local position: (localX, -0.6, 1.65) — rotate by yaw
        const wx = localX * cosY + 1.65 * sinY;
        const wz = -localX * sinY + 1.65 * cosY;
        for (let emit = 0; emit < BUBBLES_PER_THRUSTER; emit += 1) {
          if (this.bubblePool.length >= MAX_BUBBLES) {
            break;
          }
          const size = 0.055 + Math.random() * 0.07;
          const mesh = new THREE.Mesh(this.bubbleGeometry, this.bubbleMaterialPrototype.clone());
          mesh.scale.setScalar(size / 0.05);
          mesh.position.set(
            wx + (Math.random() - 0.5) * 0.06,
            -0.58 + (Math.random() - 0.5) * 0.24,
            wz + (Math.random() - 0.5) * 0.06
          );
          this.thrusterBubbleGroup.add(mesh);
          this.bubblePool.push({
            mesh,
            life: 0,
            maxLife: 1.6 + Math.random() * 1.2,
            vx: backwardX * (0.23 + Math.random() * 0.15) + (Math.random() - 0.5) * 0.045,
            vy: 0.004 + Math.random() * 0.01,
            vz: backwardZ * (0.23 + Math.random() * 0.15) + (Math.random() - 0.5) * 0.045,
            baseVy: 0.004 + Math.random() * 0.01
          });
        }
      }
    }

    // Update existing bubbles
    for (let i = this.bubblePool.length - 1; i >= 0; i -= 1) {
      const b = this.bubblePool[i];
      b.life += dt;
      const t = b.life / b.maxLife;
      const horizontalDrag = 0.976;
      b.vx *= horizontalDrag;
      b.vz *= horizontalDrag;
      // Keep bubbles pushed out first, then let buoyancy dominate as they age.
      const riseBlend = THREE.MathUtils.smoothstep(t, 0.3, 1);
      b.vy = THREE.MathUtils.lerp(b.baseVy, 0.09, riseBlend);
      b.mesh.position.x += b.vx;
      b.mesh.position.y += b.vy;
      b.mesh.position.z += b.vz;
      (b.mesh.material as THREE.MeshBasicMaterial).opacity = 0.55 * (1 - t * t);
      if (b.life >= b.maxLife) {
        this.thrusterBubbleGroup.remove(b.mesh);
        this.bubblePool.splice(i, 1);
      }
    }
  }
}
